import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import './ServicePage.css';
import './WebDevelopmentPage.css';

const SEO = {
  title: 'Web Development Services — E-commerce, CRM, CMS & Custom Software | MMC',
  description: 'Professional web development for e-commerce stores, corporate websites, custom CMS, CRM systems, SaaS platforms, landing pages and progressive web apps. Built for performance, conversion, and scale.',
  canonical: 'https://madmarketingclub.com/services/web-development',
  breadcrumb: [
    { name: 'Home',           url: 'https://madmarketingclub.com/' },
    { name: 'Services',       url: 'https://madmarketingclub.com/#services' },
    { name: 'Web Development',url: 'https://madmarketingclub.com/services/web-development' },
  ],
  service: { name: 'Web Development Services', type: 'Web Development' },
};

const HERO_TECH = [
  { label: 'React',      color: '#61dafb', delay: '3s'   },
  { label: 'Next.js',    color: '#e2e8f0', delay: '4s'   },
  { label: 'Node.js',    color: '#68a063', delay: '5s'   },
  { label: 'Shopify',    color: '#96bf48', delay: '3.5s' },
  { label: 'WordPress',  color: '#21759b', delay: '4.5s' },
  { label: 'TypeScript', color: '#3178c6', delay: '5.5s' },
  { label: 'Tailwind',   color: '#38bdf8', delay: '3.2s' },
  { label: 'MongoDB',    color: '#47a248', delay: '4.8s' },
];

const HERO_STATS = [
  { val: '200+', lbl: 'Projects Delivered' },
  { val: '3x',   lbl: 'Faster Load Times'  },
  { val: '98%',  lbl: 'Client Satisfaction'},
  { val: '50%',  lbl: 'Conversion Lift'    },
];

const BUILD_TYPES = [
  {
    title: 'E-commerce Stores',
    desc: 'Shopify, WooCommerce & custom',
    gradient: 'linear-gradient(135deg,#10b981,#059669)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
      </svg>
    ),
    points: ['Product catalog & variants','Secure checkout flows','Inventory & order tracking','Mobile-first shopping UX'],
  },
  {
    title: 'Portfolio Websites',
    desc: 'Personal, agency & creative',
    gradient: 'linear-gradient(135deg,#8b5cf6,#7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2"/>
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      </svg>
    ),
    points: ['Project showcase layouts','Case study sections','Smooth micro-animations','Contact & inquiry forms'],
  },
  {
    title: 'Custom CMS',
    desc: 'WordPress, Headless & custom-built',
    gradient: 'linear-gradient(135deg,#3b82f6,#2563eb)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
      </svg>
    ),
    points: ['Easy content management','Multi-admin access','SEO-optimised structure','Blog & media systems'],
  },
  {
    title: 'Custom CRM Systems',
    desc: 'Sales pipelines & operations',
    gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    points: ['Lead tracking dashboards','Customer lifecycle views','Task & follow-up automation','Sales & revenue reporting'],
  },
  {
    title: 'SaaS & Web Apps',
    desc: 'Dashboards, platforms & tools',
    gradient: 'linear-gradient(135deg,#ef4444,#dc2626)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
    points: ['Multi-tenant architecture','Subscription & billing','User roles & permissions','Third-party API integrations'],
  },
  {
    title: 'Landing Pages',
    desc: 'Lead gen & conversion funnels',
    gradient: 'linear-gradient(135deg,#ec4899,#db2777)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    points: ['Conversion-focused layouts','A/B test-ready structure','Lead capture & CRM sync','Page speed optimised'],
  },
  {
    title: 'Corporate Websites',
    desc: 'B2B, enterprise & companies',
    gradient: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    points: ['Brand authority design','Services & team sections','SEO-ready architecture','Trust signal integration'],
  },
  {
    title: 'Progressive Web Apps',
    desc: 'Fast, app-like experiences',
    gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
    ),
    points: ['Offline-first capability','Push notification support','Installable on any device','Near-native performance'],
  },
];

const SERVICE_TABS = [
  { key: 'ecommerce', label: 'E-commerce'       },
  { key: 'business',  label: 'Business Sites'    },
  { key: 'software',  label: 'Custom Software'   },
];

const ECOMMERCE_SERVICES = [
  'Shopify Store Development','WooCommerce Development','Custom E-commerce Builds',
  'Product Catalog Setup','Payment Gateway Integration','Mobile Checkout Optimisation',
  'Abandoned Cart Recovery','Multi-currency Support','Inventory Management',
  'Third-party App Integrations','Store Speed Optimisation','Post-launch Support',
];

const BUSINESS_SERVICES = [
  'Corporate Website Design','WordPress Development','Portfolio & Agency Sites',
  'Headless CMS Development','Multi-language Support','Blog & Content Systems',
  'Lead Generation Forms','SEO Architecture Setup','Third-party CRM Integration',
  'Analytics & Tracking Setup','Accessibility Compliance','Ongoing Maintenance',
];

const SOFTWARE_SERVICES = [
  'Custom CRM Development','SaaS Platform Development','Admin Dashboard Builds',
  'User Authentication Systems','Subscription & Billing Integration','REST API Development',
  'GraphQL API Design','Database Architecture','Microservices & Serverless',
  'Progressive Web App (PWA)','Third-party API Integration','Scalable Cloud Deployment',
];

const PROCESS = [
  {
    num: '01', title: 'Discovery & Strategy',
    intro: 'We start by understanding:',
    points: ['Business goals','Target audience','Feature requirements','Tech stack selection','Project timeline','Budget planning'],
  },
  {
    num: '02', title: 'Design & Prototype',
    intro: 'Our design process includes:',
    points: ['Wireframing','UI/UX design','Design system creation','Responsive mockups','Client review & approval','Interactive prototypes'],
  },
  {
    num: '03', title: 'Development',
    intro: 'We build with precision:',
    points: ['Frontend development','Backend & API build','CMS/CRM integration','Database setup','Third-party integrations','Performance optimisation'],
  },
  {
    num: '04', title: 'Testing & Launch',
    intro: 'Before going live:',
    points: ['Cross-browser testing','Mobile responsiveness','Performance audit','SEO setup','Security review','Deployment & go-live'],
  },
  {
    num: '05', title: 'Support & Growth',
    intro: 'After launch we provide:',
    points: ['Ongoing maintenance','Feature additions','Performance monitoring','Content updates','Scaling support','Monthly reporting'],
  },
];

const DIFFERENTIATORS = [
  'Conversion-first design philosophy',
  'Mobile-optimised development',
  'SEO-ready code architecture',
  'Performance-optimised builds',
  'Scalable, maintainable codebases',
  'Integrated analytics & tracking',
  'Full-stack in-house team',
  'Post-launch support included',
];

const TECH_CATEGORIES = [
  {
    label: 'Frontend',
    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af',
    items: ['React','Next.js','Vue.js','TypeScript','Tailwind CSS','HTML5 / CSS3'],
  },
  {
    label: 'Backend',
    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', text: '#166534',
    items: ['Node.js','Express.js','PHP','Python','REST APIs','GraphQL'],
  },
  {
    label: 'Platforms',
    color: '#b45309', bg: '#fefce8', border: '#fde68a', text: '#92400e',
    items: ['Shopify','WordPress','WooCommerce','Webflow','Strapi','Sanity'],
  },
  {
    label: 'Database',
    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', text: '#166534',
    items: ['MongoDB','PostgreSQL','MySQL','Firebase','Redis','Supabase'],
  },
  {
    label: 'Cloud & DevOps',
    color: '#c2410c', bg: '#fff7ed', border: '#fed7aa', text: '#9a3412',
    items: ['AWS','Vercel','Netlify','DigitalOcean','Docker','GitHub CI/CD'],
  },
];

const INDUSTRIES = [
  'E-commerce Brands','D2C Startups','Fashion & Apparel','Beauty & Skincare',
  'Real Estate','SaaS Companies','Professional Services','Healthcare',
  'Education & EdTech','Restaurants & Food','Finance & Fintech','Manufacturers',
];

const FAQS = [
  {
    q: 'What is the difference between Shopify development and custom e-commerce development?',
    a: 'Shopify is a hosted platform best suited for most e-commerce businesses — it is faster to launch, easier to manage, and highly scalable. Custom development is ideal for businesses with unique workflows, specialised integrations, or requirements that standard platforms cannot meet. We help you choose the right approach for your business goals.',
  },
  {
    q: 'How long does a web development project typically take?',
    a: 'Timelines vary by complexity. A landing page takes 1–2 weeks. A full e-commerce store takes 4–8 weeks. A custom CRM or SaaS platform can take 8–16 weeks or more depending on features. We provide a detailed timeline after the initial discovery phase.',
  },
  {
    q: 'Do you provide support and maintenance after launch?',
    a: 'Yes. We offer ongoing maintenance, performance monitoring, content updates, security patches, and feature additions post-launch. Long-term partnerships ensure your website keeps performing as your business grows.',
  },
  {
    q: 'Can you migrate our existing website to a new platform?',
    a: 'Yes. We handle migrations including WooCommerce to Shopify, WordPress rebuilds, custom CMS migrations, and more. Data migration, SEO preservation, and zero-downtime transitions are all part of our process.',
  },
  {
    q: 'Do you build mobile applications as well?',
    a: 'We specialise in Progressive Web Apps (PWAs) which provide an app-like experience on mobile without app store publishing. For native iOS or Android apps, we can advise on the right approach or partner with specialised teams.',
  },
];

export default function WebDevelopmentPage() {
  const [activeTab,    setActiveTab]    = useState('ecommerce');
  const [transitioning,setTransitioning]= useState(false);
  const [openStep,     setOpenStep]     = useState(0);
  const [openFaq,      setOpenFaq]      = useState(null);

  const switchTab = (t) => {
    if (t === activeTab) return;
    setTransitioning(true);
    setTimeout(() => { setActiveTab(t); setTransitioning(false); }, 150);
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Web Development</span>
          </div>
        </nav>

        {/* ── HERO ── */}
        <section className="wd-hero">
          <div className="wd-orb wd-orb-1" />
          <div className="wd-orb wd-orb-2" />
          <div className="wd-orb wd-orb-3" />
          <div className="wd-hero-inner">
            <div className="pm-hero-badges">
              {HERO_TECH.map((t, i) => (
                <span key={i} className="pm-hero-badge" style={{ '--badge-delay': t.delay }}>
                  <span className="pm-hero-badge-dot" style={{ background: t.color }} />
                  {t.label}
                </span>
              ))}
            </div>
            <div className="sp-hero-tag">Web Development</div>
            <h1 className="wd-hero-h1">
              Web Development Built for Performance &amp; Growth
            </h1>
            <p className="wd-hero-sub">
              E-commerce · Corporate Websites · Custom CMS · CRM Systems · SaaS Platforms
            </p>
            <p className="sp-hero-desc">
              We design and develop high-performance websites, e-commerce stores, custom CRM systems, SaaS platforms, and progressive web apps. Every project is built for speed, conversion, scalability, and long-term business growth — not just aesthetics.
            </p>
            <div className="pm-hero-stats">
              {HERO_STATS.map((s, i) => (
                <div key={i} className="pm-hero-stat">
                  <span className="pm-hero-stat-val">{s.val}</span>
                  <span className="pm-hero-stat-lbl">{s.lbl}</span>
                </div>
              ))}
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Start Your Project</a>
              <a href="#what-we-build" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* ── WHAT WE BUILD ── */}
        <section className="sp-sec" id="what-we-build">
          <div className="sp-sec-inner">
            <div className="sp-label">What We Build</div>
            <h2 className="sp-h2">Complete Web Development Solutions</h2>
            <p className="sp-para">
              From Shopify stores to complex CRM systems and SaaS platforms — every solution is tailored to your business, built for performance, and designed to convert.
            </p>
            <div className="wd-build-grid">
              {BUILD_TYPES.map((item, i) => (
                <div key={i} className="wd-build-card">
                  <div className="wd-build-card-top">
                    <div className="wd-build-icon" style={{ background: item.gradient }}>
                      {item.icon}
                    </div>
                    <div>
                      <div className="wd-build-title">{item.title}</div>
                      <div className="wd-build-desc">{item.desc}</div>
                    </div>
                  </div>
                  <ul className="wd-build-list">
                    {item.points.map((pt, j) => (
                      <li key={j} className="wd-build-item">
                        <span className="sp-dot-sm" />{pt}
                      </li>
                    ))}
                  </ul>
                  <div className="wd-build-accent" style={{ background: item.gradient }} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURED SERVICES TABS ── */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Detailed Service Breakdown</div>
            <h2 className="sp-h2">Everything We Build, In One Place</h2>

            <div className="pm-tabs-wrap">
              <div className="pm-tabs-nav">
                {SERVICE_TABS.map(tab => (
                  <button
                    key={tab.key}
                    className={`pm-tab-btn wd-tab-btn--${tab.key}${activeTab === tab.key ? ' pm-tab-btn--active' : ''}`}
                    onClick={() => switchTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className={`pm-tab-content${transitioning ? ' pm-tab-content--fade' : ''}`}>

                {activeTab === 'ecommerce' && (
                  <div className="wd-service-panel">
                    <div className="wd-panel-content">
                      <div className="pm-platform-header">
                        <div className="pm-platform-icon" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="pm-platform-title">E-commerce Development</h3>
                          <p className="pm-platform-sub">Shopify, WooCommerce &amp; Custom Online Stores</p>
                        </div>
                      </div>
                      <p className="sp-para">
                        We build e-commerce stores designed to sell — not just look good. Whether it is Shopify for modern D2C brands, WooCommerce for flexible catalogues, or fully custom builds, our focus is always on checkout speed, mobile UX, and conversion performance.
                      </p>
                      <div className="pm-services-label">Our E-commerce Services Include</div>
                      <div className="pm-services-grid">
                        {ECOMMERCE_SERVICES.map((s, i) => (
                          <div key={i} className="pm-service-chip wd-chip--ecommerce">{s}</div>
                        ))}
                      </div>
                    </div>
                    <div className="wd-panel-img">
                      <img
                        src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80"
                        alt="E-commerce development"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'business' && (
                  <div className="wd-service-panel">
                    <div className="wd-panel-content">
                      <div className="pm-platform-header">
                        <div className="pm-platform-icon" style={{ background: 'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                            <polyline points="9 22 9 12 15 12 15 22"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="pm-platform-title">Business &amp; Portfolio Sites</h3>
                          <p className="pm-platform-sub">Corporate, CMS &amp; Brand Authority Websites</p>
                        </div>
                      </div>
                      <p className="sp-para">
                        Your website is often the first impression of your brand. We build corporate websites, portfolios, and CMS-driven platforms that communicate authority, build trust, and generate leads — all on a solid SEO foundation.
                      </p>
                      <div className="pm-services-label">Our Business Site Services Include</div>
                      <div className="pm-services-grid">
                        {BUSINESS_SERVICES.map((s, i) => (
                          <div key={i} className="pm-service-chip wd-chip--business">{s}</div>
                        ))}
                      </div>
                    </div>
                    <div className="wd-panel-img">
                      <img
                        src="https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&q=80"
                        alt="Business website development"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'software' && (
                  <div className="wd-service-panel">
                    <div className="wd-panel-content">
                      <div className="pm-platform-header">
                        <div className="pm-platform-icon" style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="16 18 22 12 16 6"/>
                            <polyline points="8 6 2 12 8 18"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="pm-platform-title">Custom Software &amp; Web Apps</h3>
                          <p className="pm-platform-sub">CRM, SaaS, Dashboards &amp; Custom Platforms</p>
                        </div>
                      </div>
                      <p className="sp-para">
                        When off-the-shelf tools do not fit your workflow, we build the software you need. Custom CRMs, SaaS platforms, internal dashboards, and web apps — all designed around your exact business processes and built to scale.
                      </p>
                      <div className="pm-services-label">Our Custom Software Services Include</div>
                      <div className="pm-services-grid">
                        {SOFTWARE_SERVICES.map((s, i) => (
                          <div key={i} className="pm-service-chip wd-chip--software">{s}</div>
                        ))}
                      </div>
                    </div>
                    <div className="wd-panel-img">
                      <img
                        src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80"
                        alt="Custom software development"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* ── PROCESS ── */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Development Process</div>
            <h2 className="sp-h2">How We Build Your Project</h2>
            <div className="sp-proc-acc">
              {PROCESS.map((step, i) => (
                <div key={i} className={`sp-proc-acc-item${openStep === i ? ' sp-proc-acc-item--open' : ''}`}>
                  <button
                    className="sp-proc-acc-header"
                    onClick={() => setOpenStep(openStep === i ? null : i)}
                    aria-expanded={openStep === i}
                  >
                    <span className="sp-proc-acc-num">{step.num}</span>
                    <span className="sp-proc-acc-title">{step.title}</span>
                    <svg className="sp-proc-acc-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {openStep === i && (
                    <div className="sp-proc-acc-body">
                      <p className="sp-proc-acc-intro">{step.intro}</p>
                      <ul className="sp-proc-acc-grid">
                        {step.points.map((pt, j) => (
                          <li key={j} className="sp-proc-acc-pt">
                            <span className="sp-dot-sm" />{pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY CHOOSE + TECH STACK ── */}
        <section className="sp-sec sp-sec--alt" id="why">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Development Team</div>
            <h2 className="sp-h2">Built for Results, Not Just Aesthetics</h2>

            <div className="pm-why-grid">
              <div>
                <h3 className="sp-h3">What Makes Our Approach Different</h3>
                <ul className="pm-diff-list">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="pm-diff-item">
                      <span className="sp-check" />{d}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pm-stats-display">
                {HERO_STATS.map((s, i) => (
                  <div key={i} className="pm-stat-card">
                    <span className="pm-stat-val">{s.val}</span>
                    <span className="pm-stat-lbl">{s.lbl}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sp-label" style={{ marginTop: '36px' }}>Tech Stack</div>
            <h3 className="sp-h3">Technologies We Build With</h3>
            <div className="wd-tech-grid">
              {TECH_CATEGORIES.map((cat, i) => (
                <div key={i} className="wd-tech-category">
                  <div className="wd-tech-cat-label" style={{ background: cat.bg, borderColor: cat.border, color: cat.text }}>
                    <span className="wd-tech-cat-dot" style={{ background: cat.color }} />
                    {cat.label}
                  </div>
                  <div className="wd-tech-chips">
                    {cat.items.map((item, j) => (
                      <span key={j} className="wd-tech-chip" style={{ background: cat.bg, borderColor: cat.border, color: cat.text }}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <h3 className="sp-h3" style={{ marginTop: '36px' }}>Industries We Work With</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => (
                <span key={i} className="sp-industry-chip">{ind}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggleFaq(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="sp-faq-body">
                      <div className="sp-faq-a">{faq.a}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="wd-cta">
          <div className="wd-cta-orb wd-cta-orb-1" />
          <div className="wd-cta-orb wd-cta-orb-2" />
          <div className="pm-cta-inner">
            <div className="pm-cta-badge">Start Building</div>
            <h2 className="pm-cta-h2">Ready to Build Something Exceptional?</h2>
            <p className="pm-cta-desc">
              Whether you need a high-converting e-commerce store, a powerful CRM system, or a custom SaaS platform — we build digital products that perform. Let us bring your vision to life with clean code, great design, and a strategy focused on your growth.
            </p>
            <div className="pm-cta-btns">
              <a href="/#contact" className="sp-btn-white">Start Your Project</a>
              <a href="#what-we-build" className="pm-cta-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
